import { getQuery } from '@/components/filter/helpers';
import { BaseApiClient } from '@/services/baseApiClient';
import { RequestError, type RequestOptions, uploadFile } from '@/utils/request';
import { ImportUsersFormField, type UserPopulate } from '@enmon/commons/lib/enmon';
import { captureException } from '@sentry/react';
import type { Args, Results } from '.';

export class UsersApiClient extends BaseApiClient {
  constructor() {
    super('api/users');
  }

  public async create(payload: Args.CreateUserArgs): Promise<Results.CreateUserResult> {
    return await this.http.post('', payload);
  }

  public async get<TPopulate extends UserPopulate>(
    filter: Args.GetUsersArgs<TPopulate>,
    signal: RequestOptions['signal'],
  ): Promise<Results.GetUsersResult<TPopulate>> {
    return await this.http.get('', {
      query: getQuery(filter),
      signal,
    });
  }

  public async getOne<TPopulate extends UserPopulate>(
    { userId, filter }: Args.GetUserArgs<TPopulate>,
    signal: RequestOptions['signal'],
  ): Promise<Results.GetUserResult<TPopulate>> {
    return await this.http.get(`${userId}`, {
      query: getQuery(filter),
      signal,
    });
  }

  public async getMe(signal: RequestOptions['signal']): Promise<Results.GetMeResult> {
    return await this.http.get('me', { signal });
  }

  public async getMyAccess(
    filter: Args.GetMyAccessArgs,
    signal: RequestOptions['signal'],
  ): Promise<Results.GetMyAccessResult> {
    return await this.http.get('me/access', {
      query: getQuery(filter),
      signal,
    });
  }

  public async update({ userId, payload }: Args.UpdateUserArgs): Promise<Results.UpdateUserResult> {
    return await this.http.patch(`${userId}`, payload);
  }

  public async updateMe(
    payload: Args.UpdateMySettingsArgs | Args.ChangeMyPasswordArgs,
  ): Promise<Results.UpdateMeResult> {
    return await this.http.patch('me', payload);
  }

  public async changePassword({
    userId,
    payload,
  }: Args.ChangeUserPasswordArgs): Promise<Results.ChangeUserPasswordResult> {
    return await this.http.put(`${userId}/change-password`, payload);
  }

  public async resetPassword(userId: Args.ResetUserPasswordArgs): Promise<Results.ResetUserPasswordResult> {
    return await this.http.put(`${userId}/reset-password`, {});
  }

  public async delete(userId: Args.DeleteUserArgs): Promise<Results.DeleteUserResult> {
    return await this.http.delete(`${userId}`);
  }

  public async importUsers({ importFile }: Args.ImportUsersArg): Promise<Results.ImportUsersResult> {
    const formData = new FormData();
    formData.append(ImportUsersFormField.ImportFile, importFile);

    try {
      return await uploadFile(`${this.http.urlPrefix}/import`, formData, {
        signal: undefined,
      });
    } catch (err) {
      // TODO: https://enmontech.atlassian.net/browse/ENMON-1666
      if (err instanceof RequestError && err.response.status === 412) {
        return await err.response.json<Results.ImportUsersResult>();
      }
      console.error(err);
      captureException(err);
      throw err;
    }
  }

  public async disableUserIdTwoFactorAuth({
    userId,
  }: Args.DisableUserIdTwoFactorAuthArgs): Promise<Results.DisableUserIdTwoFactorAuthResult> {
    return await this.http.put(`${userId}/disable-two-factor`, {});
  }
}
